import { CompareActionButton } from '@modules/quantify/components/CompareActionButton';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { isValidQuery } from '@modules/quantify/utils';
import { ExpandCircleDownRounded } from '@mui/icons-material';
import { alpha, Box, IconButton, Paper, Stack } from '@mui/material';
import { QuantifyQueryBuilderPreview } from './QuantifyQueryBuilderPreview';
import { QuantifyQueryComposer } from './QuantifyQueryComposer';

interface QuantifyQueryBuilderProps {
  shouldSkipCompareCTA?: boolean;
}

export function QuantifyQueryBuilder({
  shouldSkipCompareCTA = false,
}: QuantifyQueryBuilderProps) {
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const query = useQuantifyStore(({ query }) => query);
  const isEditMode = useQuantifyStore(({ visual }) => visual.isEditMode);

  function toggleShouldExpandQueryBuilder() {
    dispatch({
      type: QuantifyActionType.SET_VISUAL,
      payload: {
        isEditMode: !isEditMode,
      },
    });
  }

  const shouldDisableExpandButton = isEditMode ? !isValidQuery(query) : false;

  return (
    <Stack
      data-testid="quantify-query-builder"
      spacing={3}
      alignItems="flex-start"
      sx={{
        position: 'sticky',
        top: 20,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          cursor: isEditMode ? undefined : 'pointer',
          '&:hover': {
            backgroundColor: isEditMode
              ? undefined
              : (theme) => alpha(theme.palette.primary.main, 0.05),
          },
          pb: 1,
        }}
      >
        <IconButton
          disabled={shouldDisableExpandButton}
          color="primary"
          size="large"
          onClick={toggleShouldExpandQueryBuilder}
          sx={{ position: 'absolute', right: '-28px', top: 0 }}
        >
          <ExpandCircleDownRounded
            fontSize="large"
            sx={{
              transform: isEditMode ? 'rotate(90deg)' : 'rotate(-90deg)',
            }}
          />
        </IconButton>
        <Box display={isEditMode ? 'unset' : 'none'}>
          <QuantifyQueryComposer />
        </Box>
        <Box display={isEditMode ? 'none' : 'unset'}>
          <QuantifyQueryBuilderPreview />
        </Box>
      </Paper>
      {isEditMode && !shouldSkipCompareCTA ? <CompareActionButton /> : null}
    </Stack>
  );
}
